import { ArrowLeftOutlined } from '@ant-design/icons'
import type { MouseEvent, ReactNode } from 'react'

import { Col } from '../Col'
import { AppLink } from '../Link'
import { Row } from '../Row'
import { TextSkeleton } from '../skeletons/TextSkeleton'
import { Space } from '../Space'
import { Typography } from '../Typography'

import { SKELETONS_TEST_ID } from '@/utils/constants/testing/testsIds'
import { useLocation, useNavigate } from 'react-router'
import styles from './styles.scss'

interface GoToPathProps {
  backArrowUrl: string
  fallbackUrl?: never
  showBackArrow: true
  onClickArrow?: never
}
interface GoBackProps {
  backArrowUrl?: never
  fallbackUrl: string
  showBackArrow: true
  onClickArrow?: never
}
interface NoBackArrowProps {
  backArrowUrl?: never
  fallbackUrl?: never
  showBackArrow?: false
  onClickArrow?: never
}
interface CustomHandler {
  backArrowUrl?: never
  fallbackUrl?: never
  onClickArrow: () => void
  showBackArrow: true
}

type BackArrowProps = GoToPathProps | GoBackProps | NoBackArrowProps | CustomHandler

export type PageHeaderProps = {
  children?: ReactNode
  direction?: 'row' | 'column'
  loading?: boolean
  title: string
  titleFlex?: number
  titleVariant?: 'header' | 'text'
  subtitle?: string
  subTitleFlex?: number
} & BackArrowProps

export const PAGE_HEADER_TEST_ID = 'page-header-test-id'

export function PageHeader({
  backArrowUrl = '',
  children,
  direction = 'row',
  fallbackUrl,
  loading = false,
  showBackArrow = false,
  title,
  titleFlex,
  titleVariant = 'header',
  subtitle,
  subTitleFlex,
  onClickArrow,
  ...props
}: PageHeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()

  function arrowHandler(e: MouseEvent<HTMLAnchorElement>) {
    if (onClickArrow) {
      e.preventDefault()
      onClickArrow()
      return
    }

    handleGoBack(e)
  }

  function handleGoBack(e: MouseEvent<HTMLAnchorElement>) {
    if (!showBackArrow || !fallbackUrl || backArrowUrl) return

    e.preventDefault()
    const hasPreviousPage = location.key !== 'default'

    if (hasPreviousPage) {
      navigate(-1)
    } else {
      navigate(fallbackUrl)
    }
  }

  return (
    <Row
      className={styles.header}
      data-testid={PAGE_HEADER_TEST_ID}
      gutter={[8, 16]}
      align="middle"
      {...props}
    >
      <Col flex={!subTitleFlex && !titleFlex ? 1 : titleFlex}>
        <Space direction="vertical">
          <Space align="center">
            {showBackArrow && (
              <AppLink className={styles.backArrowLink} onClick={arrowHandler} to={backArrowUrl}>
                <ArrowLeftOutlined />
              </AppLink>
            )}
            {loading ? (
              <TextSkeleton data-testid={SKELETONS_TEST_ID} width="15rem" />
            ) : titleVariant === 'header' ? (
              <Typography.Title level={4} noMargin>
                {title}
              </Typography.Title>
            ) : (
              <Typography.Text noMargin>{title}</Typography.Text>
            )}
          </Space>

          {subtitle && <Typography.Paragraph type="secondary">{subtitle}</Typography.Paragraph>}
        </Space>
      </Col>

      {children && (
        <Col flex={subTitleFlex} span={direction === 'column' ? 24 : undefined}>
          {children}
        </Col>
      )}
    </Row>
  )
}
